/**
 * +world-resources: one additive instanced batch for every loot glow (one draw call). Shapes are
 * procedural in the fragment shader (no texture):
 *   GLOW  soft camera-facing glow            BEAM  vertical light beam (cylindrical billboard)
 *   RING  flat ground ring (param = width)    DISC  flat soft ground glow
 *   ARC   flat progress ring (param = 0..1)   STAR  4-point twinkle (rot = iSize.w)
 * The batch is refilled every frame (begin / push / end) from preallocated typed arrays.
 */
import * as THREE from 'three';

export const SHAPE = { GLOW: 0, BEAM: 1, RING: 2, DISC: 3, ARC: 4, STAR: 5 } as const;

const VERT = `
attribute vec4 iPos; attribute vec4 iSize; attribute vec4 iCol;
uniform float uFx;
varying vec2 vUv; varying vec4 vCol; varying float vShape; varying float vParam;
void main(){
  vec2 c = position.xy;
  float shape = iPos.w;
  vUv = c * 2.0;
  vCol = vec4(iCol.rgb, iCol.a * uFx);
  vShape = shape;
  vParam = iSize.z;
  if (shape < 0.5 || shape > 4.5) {
    vec4 mv = modelViewMatrix * vec4(iPos.xyz, 1.0);
    float r = iSize.w; float cs = cos(r), sn = sin(r);
    mv.xy += vec2(c.x * cs - c.y * sn, c.x * sn + c.y * cs) * iSize.xy;
    gl_Position = projectionMatrix * mv;
  } else if (shape < 1.5) {
    vec3 toCam = cameraPosition - iPos.xyz;
    vec3 right = normalize(vec3(toCam.z, 0.0, -toCam.x) + vec3(1e-5, 0.0, 0.0));
    vec3 wp = iPos.xyz + right * c.x * iSize.x + vec3(0.0, (c.y + 0.5) * iSize.y, 0.0);
    vUv = vec2(c.x * 2.0, c.y + 0.5);
    gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
  } else {
    vec3 wp = iPos.xyz + vec3(c.x * iSize.x * 2.0, 0.0, c.y * iSize.x * 2.0);
    gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
  }
}`;

const FRAG = `
varying vec2 vUv; varying vec4 vCol; varying float vShape; varying float vParam;
void main(){
  float d = length(vUv);
  float a = 0.0;
  if (vShape < 0.5) {
    float k = clamp(1.0 - d, 0.0, 1.0);
    a = k * k * (0.6 + 0.4 * k);
  } else if (vShape < 1.5) {
    // pow() of a negative base is undefined (NaN on some GPUs, which bloom smears into black
    // blocks): keep every base in [0, 1].
    float x = clamp(abs(vUv.x), 0.0, 1.0);
    float y = clamp(vUv.y, 0.0, 1.0);
    a = pow(1.0 - x, 2.2) * pow(1.0 - y, 1.6) * smoothstep(0.0, 0.08, y);
    a += pow(1.0 - clamp(x * 3.0, 0.0, 1.0), 2.0) * (1.0 - y) * 0.6;
  } else if (vShape < 2.5) {
    float w = max(0.02, vParam);
    a = smoothstep(1.0 - w, 1.0 - w * 0.5, d) * (1.0 - smoothstep(0.96, 1.0, d));
  } else if (vShape < 3.5) {
    float k = clamp(1.0 - d, 0.0, 1.0);
    a = k * k;
  } else if (vShape < 4.5) {
    float band = smoothstep(0.72, 0.78, d) * (1.0 - smoothstep(0.94, 1.0, d));
    float ang = atan(vUv.x, vUv.y) / 6.2831853 + 0.5;
    float fill = step(ang, vParam);
    a = band * (0.22 + 0.78 * fill);
  } else {
    vec2 p = abs(vUv);
    a = max(exp(-p.x * 14.0) * exp(-p.y * 2.6), exp(-p.y * 14.0) * exp(-p.x * 2.6));
    a = a * clamp(1.0 - d, 0.0, 1.0) + 0.35 * pow(clamp(1.0 - d * 1.6, 0.0, 1.0), 2.0);
  }
  a *= vCol.a;
  if (a < 0.003) discard;
  gl_FragColor = vec4(max(vCol.rgb * a, vec3(0.0)), 1.0);
}`;

export class GlowSprites {
  readonly mesh: THREE.Mesh;
  readonly capacity: number;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly mat: THREE.ShaderMaterial;
  private readonly pos: Float32Array;
  private readonly size: Float32Array;
  private readonly col: Float32Array;
  private readonly attrs: THREE.InstancedBufferAttribute[];
  private n = 0;
  readonly uFx = { value: 1 };

  constructor(capacity = 384) {
    this.capacity = capacity;
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.setAttribute('position', quad.getAttribute('position'));
    this.pos = new Float32Array(capacity * 4);
    this.size = new Float32Array(capacity * 4);
    this.col = new Float32Array(capacity * 4);
    const mk = (a: Float32Array, name: string): THREE.InstancedBufferAttribute => {
      const at = new THREE.InstancedBufferAttribute(a, 4);
      at.setUsage(THREE.DynamicDrawUsage);
      this.geo.setAttribute(name, at);
      return at;
    };
    this.attrs = [mk(this.pos, 'iPos'), mk(this.size, 'iSize'), mk(this.col, 'iCol')];
    this.geo.instanceCount = 0;
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uFx: this.uFx },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.name = 'lootGlow';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 8;
  }

  begin(): void {
    this.n = 0;
  }

  /** Queue one sprite (silently dropped when full). Colours are linear RGB; alpha scales it. */
  push(shape: number, x: number, y: number, z: number, w: number, h: number, param: number, rot: number, r: number, g: number, b: number, a: number): void {
    if (this.n >= this.capacity || a <= 0.002) return;
    const o = this.n * 4;
    this.pos[o] = x;
    this.pos[o + 1] = y;
    this.pos[o + 2] = z;
    this.pos[o + 3] = shape;
    this.size[o] = w;
    this.size[o + 1] = h;
    this.size[o + 2] = param;
    this.size[o + 3] = rot;
    this.col[o] = r;
    this.col[o + 1] = g;
    this.col[o + 2] = b;
    this.col[o + 3] = a;
    this.n++;
  }

  /** Sprites queued this frame. */
  get count(): number {
    return this.n;
  }

  end(): void {
    this.geo.instanceCount = this.n;
    if (this.n === 0) return;
    for (const at of this.attrs) {
      at.clearUpdateRanges();
      at.addUpdateRange(0, this.n * 4);
      at.needsUpdate = true;
    }
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    this.mat.dispose();
  }
}
